import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Code, 
  BookOpen, 
  Terminal,
  Copy,
  ExternalLink,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const APIDocumentation = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [copiedCode, setCopiedCode] = useState('');

  const baseUrl = window.location.origin;
  const apiBaseUrl = 'https://ateoqefwjeivvlnfpmon.supabase.co/functions/v1/api-handler';
  
  const copyCode = (code: string, label: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(label);
    toast({
      title: "Copied!",
      description: `${label} copied to clipboard`,
    });
    setTimeout(() => setCopiedCode(''), 2000);
  };

  // REST API Examples
  const restExamples = {
    'List Forms': {
      method: 'GET',
      endpoint: '/forms',
      description: 'Retrieve all your forms',
      request: `curl -X GET '${apiBaseUrl}/forms' \\
  -H 'Authorization: Bearer YOUR_API_KEY'`,
      response: `{
  "forms": [
    {
      "id": "abc123",
      "title": "Contact Form",
      "is_published": true,
      "created_at": "2024-01-15T10:30:00Z"
    }
  ],
  "count": 1,
  "limit": 50,
  "offset": 0
}`
    },
    'Get Form': {
      method: 'GET',
      endpoint: '/forms/:id',
      description: 'Get a specific form by ID',
      request: `curl -X GET '${apiBaseUrl}/forms/abc123' \\
  -H 'Authorization: Bearer YOUR_API_KEY'`,
      response: `{
  "form": {
    "id": "abc123",
    "title": "Contact Form",
    "description": "Get in touch with us",
    "fields": [
      {
        "id": "field1",
        "type": "text",
        "label": "Name",
        "required": true
      }
    ],
    "is_published": true,
    "brand_color": "#25D366",
    "response_mode": "both"
  }
}`
    },
    'List Submissions': {
      method: 'GET',
      endpoint: '/submissions?form_id=:id',
      description: 'Get all submissions for a form',
      request: `curl -X GET '${apiBaseUrl}/submissions?form_id=abc123' \\
  -H 'Authorization: Bearer YOUR_API_KEY'`,
      response: `{
  "submissions": [
    {
      "id": "sub123",
      "form_id": "abc123",
      "data": {
        "name": "John Doe",
        "email": "john@example.com"
      },
      "submitted_at": "2024-01-20T14:30:00Z"
    }
  ],
  "count": 1,
  "limit": 50,
  "offset": 0
}`
    },
    'Get Analytics': {
      method: 'GET',
      endpoint: '/analytics?timeRange=7d',
      description: 'Get analytics data for your forms',
      request: `curl -X GET '${apiBaseUrl}/analytics?timeRange=7d' \\
  -H 'Authorization: Bearer YOUR_API_KEY'`,
      response: `{
  "totalViews": 150,
  "totalSubmissions": 45,
  "conversionRate": 30.0,
  "averageCompletionTime": 45,
  "deviceBreakdown": {
    "desktop": 100,
    "mobile": 45,
    "tablet": 5
  },
  "timeRange": "7d",
  "period": {
    "start": "2024-01-13T00:00:00Z",
    "end": "2024-01-20T00:00:00Z"
  }
}`
    },
    'List Contacts': {
      method: 'GET',
      endpoint: '/contacts',
      description: 'Get all your contacts',
      request: `curl -X GET '${apiBaseUrl}/contacts?search=john' \\
  -H 'Authorization: Bearer YOUR_API_KEY'`,
      response: `{
  "contacts": [
    {
      "id": "contact123",
      "name": "John Doe",
      "email": "john@example.com",
      "phone": "+2348012345678",
      "source": "form_submission",
      "created_at": "2024-01-20T14:30:00Z"
    }
  ],
  "count": 1,
  "limit": 50,
  "offset": 0
}`
    }
  };

  // iFrame Embed Documentation
  const iframeExamples = {
    'Basic Embed': {
      description: 'Simple iframe embed for any website',
      code: `<!-- Replace FORM_ID with your actual form ID -->
<iframe 
  src="${baseUrl}/form/FORM_ID" 
  width="100%" 
  height="600" 
  frameborder="0"
  title="Contact Form"
></iframe>`,
      notes: [
        'Replace FORM_ID with your actual form ID',
        'Adjust height based on your form size',
        'Form will automatically match your brand colors'
      ]
    },
    'Responsive Embed': {
      description: 'Responsive iframe that adapts to container size',
      code: `<div style="position: relative; padding-bottom: 75%; height: 0; overflow: hidden;">
  <iframe 
    src="${baseUrl}/form/FORM_ID"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0;"
    title="Contact Form"
  ></iframe>
</div>`,
      notes: [
        'Maintains aspect ratio across all devices',
        'Adjust padding-bottom percentage for different aspect ratios',
        '75% = 4:3 ratio, 56.25% = 16:9 ratio'
      ]
    },
    'WordPress Embed': {
      description: 'Embed in WordPress using shortcode or HTML block',
      code: `<!-- Method 1: Add to HTML block -->
<iframe 
  src="${baseUrl}/form/FORM_ID" 
  width="100%" 
  height="600" 
  frameborder="0"
  title="Contact Form"
></iframe>

<!-- Method 2: Add to functions.php for shortcode support -->
function bravax_form_shortcode($atts) {
    $atts = shortcode_atts(array(
        'id' => '',
        'height' => '600'
    ), $atts);
    
    return '<iframe src="${baseUrl}/form/' . esc_attr($atts['id']) . '" 
            width="100%" height="' . esc_attr($atts['height']) . '" 
            frameborder="0" title="Contact Form"></iframe>';
}
add_shortcode('bravax_form', 'bravax_form_shortcode');

<!-- Then use: [bravax_form id="FORM_ID" height="700"] -->`,
      notes: [
        'Use HTML block in Gutenberg editor',
        'For shortcode, add function to theme functions.php',
        'Test in preview before publishing'
      ]
    },
    'React Integration': {
      description: 'Embed in React applications',
      code: `import React from 'react';

function ContactFormEmbed({ formId }) {
  return (
    <div className="form-container">
      <iframe 
        src="${baseUrl}/form/\${formId}"
        width="100%" 
        height="600"
        frameBorder="0"
        title="Contact Form"
        style={{
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}
      />
    </div>
  );
}

// Usage
<ContactFormEmbed formId="FORM_ID" />`,
      notes: [
        'Can use state to dynamically change form',
        'Add loading states for better UX',
        'Style container to match your design'
      ]
    },
    'JavaScript Injection': {
      description: 'Load form dynamically with JavaScript',
      code: `<div id="bravax-form-container"></div>

<script>
(function() {
  const container = document.getElementById('bravax-form-container');
  const formId = 'FORM_ID'; // Replace with your form ID
  
  const iframe = document.createElement('iframe');
  iframe.src = '${baseUrl}/form/' + formId;
  iframe.width = '100%';
  iframe.height = '600';
  iframe.frameBorder = '0';
  iframe.title = 'Contact Form';
  
  container.appendChild(iframe);
})();
</script>`,
      notes: [
        'Great for conditional loading',
        'Can integrate with analytics',
        'Easy to add loading indicators'
      ]
    },
    'Next.js Integration': {
      description: 'Embed in Next.js with proper SSR handling',
      code: `'use client'
import { useEffect, useRef } from 'react';

export default function BravaxForm({ formId }) {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (containerRef.current) {
      const iframe = document.createElement('iframe');
      iframe.src = '${baseUrl}/form/' + formId;
      iframe.width = '100%';
      iframe.height = '600';
      iframe.frameBorder = '0';
      iframe.title = 'Contact Form';
      
      containerRef.current.appendChild(iframe);
    }
  }, [formId]);
  
  return <div ref={containerRef} className="form-wrapper" />;
}`,
      notes: [
        'Use client component for iframe',
        'Can use server components for surrounding content',
        'Add proper TypeScript types if needed'
      ]
    }
  };

  // API Documentation is now publicly accessible
  const content = (
    <div className="p-6 space-y-8">
        {/* Navigation for public view */}
        {!user && (
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <BookOpen className="h-6 w-6 text-primary" />
              <span className="text-xl font-bold text-foreground">API Documentation</span>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => window.location.href = '/'}>
                Back to Home
              </Button>
              <Button onClick={() => window.location.href = '/login'}>
                Sign In
              </Button>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">API Documentation</h1>
          <p className="text-muted-foreground">
            Complete guide to integrating BravaxForms with your applications
          </p>
        </div>

        <Tabs defaultValue="rest" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="rest">REST API</TabsTrigger>
            <TabsTrigger value="iframe">iFrame Embedding</TabsTrigger>
          </TabsList>

          {/* REST API Tab */}
          <TabsContent value="rest" className="space-y-6">
            {/* Authentication */}
            <Card className="card-elegant">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Code className="h-5 w-5 text-primary" />
                  <span>Authentication</span>
                </CardTitle>
                <CardDescription>
                  All API requests require authentication using your API key
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-sm mb-2">Include your API key in the Authorization header:</p>
                  <pre className="text-sm bg-background p-3 rounded border overflow-x-auto">
                    <code>Authorization: Bearer YOUR_API_KEY</code>
                  </pre>
                </div>
                <div className="flex items-start space-x-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-warning mt-0.5" />
                  <p className="text-muted-foreground">
                    Keep your API keys secure. Never expose them in client-side code or public repositories.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Endpoints */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold">API Endpoints</h2>
              {Object.entries(restExamples).map(([name, example]) => (
                <Card key={name} className="card-elegant">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center space-x-2">
                          <Badge variant="secondary">{example.method}</Badge>
                          <span>{name}</span>
                        </CardTitle>
                        <CardDescription className="mt-2">
                          <code className="text-sm">{example.endpoint}</code>
                        </CardDescription>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">{example.description}</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold">Request</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyCode(example.request, `${name} Request`)}
                          aria-label={`Copy ${name} request to clipboard`}
                        >
                          {copiedCode === `${name} Request` ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
                        <code>{example.request}</code>
                      </pre>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold">Response</h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyCode(example.response, `${name} Response`)}
                          aria-label={`Copy ${name} response to clipboard`}
                        >
                          {copiedCode === `${name} Response` ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
                        <code>{example.response}</code>
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Rate Limits */}
            <Card className="card-elegant">
              <CardHeader>
                <CardTitle>Rate Limits</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center justify-between p-3 bg-muted rounded">
                  <span>Starter Plan</span>
                  <span className="font-semibold">1,000 requests/day</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted rounded">
                  <span>Pro Plan</span>
                  <span className="font-semibold">10,000 requests/day</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* iFrame Embedding Tab */}
          <TabsContent value="iframe" className="space-y-6">
            <Card className="card-elegant bg-gradient-accent">
              <CardHeader>
                <CardTitle className="text-white">iFrame Embedding - Use Any Form Anywhere</CardTitle>
                <CardDescription className="text-white/90">
                  Embed any of your forms on any website - contact pages, landing pages, blogs, anywhere!
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-white">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 mt-0.5" />
                    <div>
                      <p className="font-semibold">Embed Any Form</p>
                      <p className="text-white/80">Contact forms, order forms, lead capture - use any form you've created</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 mt-0.5" />
                    <div>
                      <p className="font-semibold">Instant WhatsApp Notifications</p>
                      <p className="text-white/80">Get notified immediately when someone submits your embedded form</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 mt-0.5" />
                    <div>
                      <p className="font-semibold">Works Everywhere</p>
                      <p className="text-white/80">WordPress, React, Next.js, plain HTML - any website or platform</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 mt-0.5" />
                    <div>
                      <p className="font-semibold">Brand Preserved</p>
                      <p className="text-white/80">Your logo, colors, and branding are automatically maintained</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {Object.entries(iframeExamples).map(([name, example]) => (
              <Card key={name} className="card-elegant">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{name}</CardTitle>
                      <CardDescription className="mt-2">{example.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold">Code</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyCode(example.code, name)}
                        aria-label={`Copy ${name} code example to clipboard`}
                      >
                        {copiedCode === name ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
                      <code>{example.code}</code>
                    </pre>
                  </div>
                  
                  {example.notes && (
                    <div>
                      <h4 className="text-sm font-semibold mb-2">Notes</h4>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        {example.notes.map((note, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <span className="text-primary">•</span>
                            <span>{note}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Best Practices */}
            <Card className="card-elegant">
              <CardHeader>
                <CardTitle>Best Practices</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-success mt-0.5" />
                  <div>
                    <p className="font-medium">Test Before Deploying</p>
                    <p className="text-muted-foreground">Always preview your embedded form in a staging environment first</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-success mt-0.5" />
                  <div>
                    <p className="font-medium">Set Appropriate Height</p>
                    <p className="text-muted-foreground">Adjust iframe height based on your form's field count to avoid scrolling</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-success mt-0.5" />
                  <div>
                    <p className="font-medium">Mobile Optimization</p>
                    <p className="text-muted-foreground">Use responsive embed methods for better mobile experience</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-4 w-4 text-success mt-0.5" />
                  <div>
                    <p className="font-medium">Monitor Analytics</p>
                    <p className="text-muted-foreground">Track form views and submissions to optimize conversion rates</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <Card className="card-elegant bg-gradient-accent">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-white mb-1">Need Help?</h3>
                <p className="text-sm text-white/90">
                  Contact support for assistance with API integration
                </p>
              </div>
              <Button variant="secondary" onClick={() => window.location.href = 'mailto:bravaxforms@gmail.com?subject=API Support Request'}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
  );

  return (
    <>
      <Helmet>
        <title>API Documentation | Bravax Forms</title>
      </Helmet>
      {user ? (
        <DashboardLayout>{content}</DashboardLayout>
      ) : (
        <div className="min-h-screen bg-background">
          {content}
        </div>
      )}
    </>
  );
};

export default APIDocumentation;